import React, { useState, useEffect } from 'react';
import {
    X, User, MapPin, CreditCard, Upload,
    Share2, Smartphone, Mail, FileText, BadgeCheck, Eye
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';


const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phone: Yup.string().matches(/^[0-9]{10}$/, 'Must be a 10-digit number').required('Phone is required'),
    alternativePhone: Yup.string().test('is-10-digits', 'Phone must be 10 digits', val => !val || /^[0-9]{10}$/.test(val)),
    dob: Yup.date().required('Date of Birth is required'),
    gender: Yup.string().required('Gender is required'),
    currentAddress: Yup.string().required('Address is required'),
    city: Yup.string().required('City is required'),
    district: Yup.string().required('District is required'),
    state: Yup.string().required('State is required'),
    pincode: Yup.string().matches(/^[0-9]{6}$/, 'Must be 6 digits').required('Pincode is required'),
    aadhaarNumber: Yup.string().matches(/^\d{12}$/, 'Must be 12 digits').required('Aadhaar is required'),
    panNumber: Yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i, 'Invalid PAN format').required('PAN is required'),
    bankName: Yup.string().required('Bank Name is required'),
    accountName: Yup.string().required('Account Name is required'),
    accountNumber: Yup.string().required('Account Number is required'),
    ifscCode: Yup.string().required('IFSC Code is required'),
});

const INITIAL_FORM_DATA = {
    // Personal
    title: 'Mr.',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    alternativePhone: '',
    dob: '',
    gender: '',
    currentAddress: '',
    city: '',
    district: '',
    state: '',
    pincode: '',
    aadhaarNumber: '',
    panNumber: '',

    // Marketing-specific
    region: '',
    identificationNumber: '',
    baseCommission: 10,

    // Bank
    bankName: '',
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    bankInfoMethod: 'cheque',
};

const INITIAL_FILES = {
    avatar: null,
    aadhaarCard: null,
    panCard: null,
    cancelledCheque: null,
    bankPassbook: null,
    signature: null,
};

const INDIA_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',

    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir', 'Ladakh'
];

const MarketingRegisterModal = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState(INITIAL_FILES);

    const handlePreview = (e, file) => {
        e.preventDefault();
        e.stopPropagation();
        if (file) {
            const url = URL.createObjectURL(file);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
    };

    const handleFileChange = (e) => {
        const { name, files: f } = e.target;
        if (f && f[0]) setFiles(prev => ({ ...prev, [name]: f[0] }));
    };

    const handlePincodeChange = async (e) => {
        const { value } = e.target;
        const cleanVal = value.replace(/\D/g, '').slice(0, 6);
        formik.setFieldValue('pincode', cleanVal);

        if (cleanVal.length === 6) {
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${cleanVal}`);
                const data = await res.json();
                if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
                    const postOffice = data[0].PostOffice[0];
                    const districtName = postOffice.District || postOffice.Division || '';
                    const stateName = postOffice.State || '';
                    const cityName = postOffice.District || postOffice.Name || '';

                    formik.setFieldValue('city', cityName);
                    formik.setFieldValue('district', districtName);
                    formik.setFieldValue('state', stateName);
                    toast.success(`Location auto-fetched for PIN ${cleanVal}`);
                }
            } catch (err) {
                console.error("Failed to fetch pincode details", err);
            }
        }
    };

    const scrollToFirstError = (fieldName = null) => {
        setTimeout(() => {
            if (fieldName) {
                const el = document.querySelector(`[name="${fieldName}"]`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.focus?.();
                    return;
                }
            }

            const firstErrorDiv = document.querySelector('.text-red-500');
            if (firstErrorDiv) {
                firstErrorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const parentInput = firstErrorDiv.parentElement?.querySelector('input, select, textarea');
                parentInput?.focus?.();
                return;
            }

            const errorKeys = Object.keys(formik.errors);
            if (errorKeys.length > 0) {
                const firstKey = errorKeys[0];
                const input = document.querySelector(`[name="${firstKey}"]`);
                if (input) {
                    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    input.focus?.();
                }
            }
        }, 100);
    };

    const formik = useFormik({
        initialValues: INITIAL_FORM_DATA,
        validationSchema,
        onSubmit: async (values) => {
            let hasFileError = false;

            if (!files.panCard) {
                toast.error('PAN Card document file is required! Please upload the PAN Card file.');
                scrollToFirstError('panNumber');
                hasFileError = true;
            } else if (!files.aadhaarCard) {
                toast.error('Aadhaar Card document file is required! Please upload the Aadhaar Card file.');
                scrollToFirstError('aadhaarNumber');
                hasFileError = true;
            }

            if (hasFileError) return;

            setLoading(true);
            try {
                const fd = new FormData();
                fd.append('role', 'marketing_agent');

                Object.entries(values).forEach(([key, val]) => {
                    if (key === 'bankInfoMethod') return;
                    if (val !== '' && val !== null && val !== undefined) {
                        fd.append(key, val);
                    }
                });

                if (files.avatar) fd.append('avatar', files.avatar);
                if (files.aadhaarCard) fd.append('aadhaarCard', files.aadhaarCard);
                if (files.panCard) fd.append('panCard', files.panCard);
                if (files.signature) fd.append('signature', files.signature);

                const bankFile = values.bankInfoMethod === 'cheque' ? files.cancelledCheque : files.bankPassbook;
                if (bankFile) fd.append('bankDocument', bankFile);

                await api.post('/auth/register', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                toast.success('Lead Partner registered successfully!');
                localStorage.removeItem('doxez_draft_marketing_registration');
                setHasDraft(false);
                onSuccess?.();
                onClose();
            } catch (error) {
                toast.error(error?.response?.data?.message || 'Registration failed');
            } finally {
                setLoading(false);
            }
        }
    });

    const DRAFT_KEY = 'doxez_draft_marketing_registration';
    const [hasDraft, setHasDraft] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const savedDraft = localStorage.getItem(DRAFT_KEY);
            if (savedDraft) {
                try {
                    const parsed = JSON.parse(savedDraft);
                    if (parsed && typeof parsed === 'object') {
                        formik.setValues({ ...INITIAL_FORM_DATA, ...parsed });
                        setHasDraft(true);
                    }
                } catch (err) {
                    console.error("Failed to recover marketing draft", err);
                }
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (formik.values) {
            const isModified = Object.entries(formik.values).some(([key, val]) => {
                const initial = INITIAL_FORM_DATA[key];
                if (typeof val === 'object') return JSON.stringify(val) !== JSON.stringify(initial);
                return val !== '' && val !== null && val !== undefined && val !== initial;
            });

            if (isModified) {
                localStorage.setItem(DRAFT_KEY, JSON.stringify(formik.values));
                setHasDraft(true);
            }
        }
    }, [formik.values]);

    const handleClearDraft = () => {
        if (window.confirm('Are you sure you want to clear this saved draft and reset all fields?')) {
            localStorage.removeItem(DRAFT_KEY);
            formik.resetForm({ values: INITIAL_FORM_DATA });
            setFiles(INITIAL_FILES);
            setHasDraft(false);
            toast.success('Draft cleared and form reset');
        }
    };

    useEffect(() => {
        if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
            scrollToFirstError();
        }
    }, [formik.submitCount, formik.errors]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <div className="bg-white w-full max-w-5xl h-[95vh] md:h-[90vh] overflow-hidden rounded-3xl md:rounded-[40px] shadow-2xl flex flex-col relative animate-in fade-in zoom-in duration-300">

                {/* Header */}
                <div className="px-6 py-6 md:px-12 md:py-8 border-b border-slate-100 flex items-center justify-between bg-white relative z-10">
                    <div className="flex items-center gap-4 md:gap-6">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-violet-50 rounded-2xl md:rounded-[24px] flex items-center justify-center text-violet-600 shadow-inner shrink-0">
                            <Share2 className="w-6 h-6 md:w-8 md:h-8" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg md:text-2xl font-black text-[#143E78] tracking-tight italic uppercase">
                                    Lead Partner <span className="text-violet-600">Onboarding</span>
                                </h2>
                                {hasDraft && (
                                    <span className="px-2.5 py-1 bg-amber-500/10 text-amber-700 border border-amber-300/40 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-sm">
                                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> Draft Auto-Saved
                                    </span>
                                )}
                            </div>
                            <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-violet-500 animate-pulse"></span>
                                Direct Lead Partner Registration
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {hasDraft && (
                            <button
                                type="button"
                                onClick={handleClearDraft}
                                className="px-3.5 py-2 bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-600 text-xs font-black rounded-xl transition-all border border-slate-200 hover:border-rose-200 cursor-pointer"
                                title="Clear saved draft and start fresh"
                            >
                                Clear Draft
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 md:p-4 hover:bg-slate-50 rounded-full transition-all text-slate-400">
                            <X className="w-5 h-5 md:w-6 md:h-6" />
                        </button>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6 py-6 md:px-12 md:py-10 custom-scrollbar">
                    <form onSubmit={formik.handleSubmit} className="space-y-10 md:space-y-14">

                        {/* SECTION 1: PERSONAL PROFILE */}
                        <div className="bg-slate-50/35 border border-slate-100/80 p-6 md:p-10 rounded-[32px] space-y-6 md:space-y-8 shadow-sm">
                            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-[#143E78] shadow-sm">
                                    <User className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Personal Profile</h3>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-0.5">Personal identity & contact details</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
                                {/* Title + First Name row */}
                                <div className="md:col-span-2 col-span-full grid grid-cols-4 gap-4">
                                    <div className="col-span-1 space-y-2">
                                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Title <span className="text-red-500">*</span></label>
                                        <select name="title" required value={formik.values.title} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-4 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm">
                                            <option value="Mr.">Mr.</option>
                                            <option value="Ms.">Ms.</option>
                                            <option value="Mrs.">Mrs.</option>
                                            <option value="Miss.">Miss.</option>
                                            <option value="Dr.">Dr.</option>
                                        </select>
                                    {formik.touched.title && formik.errors.title && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.title}</div>)}
                                    </div>
                                    <div className="col-span-3 space-y-2">
                                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">First Name <span className="text-red-500">*</span></label>
                                        <input type="text" name="firstName" required value={formik.values.firstName} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.firstName && formik.errors.firstName && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.firstName}</div>)}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Last Name <span className="text-red-500">*</span></label>
                                    <input type="text" name="lastName" required value={formik.values.lastName} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.lastName && formik.errors.lastName && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.lastName}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Email ID <span className="text-red-500">*</span></label>
                                    <input type="email" name="email" required value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.email && formik.errors.email && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.email}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Phone (WhatsApp) <span className="text-red-500">*</span></label>
                                    <input type="text" name="phone" required value={formik.values.phone} onChange={(e) => formik.setFieldValue('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} onBlur={formik.handleBlur} maxLength={10} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.phone && formik.errors.phone && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.phone}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Alternative Phone</label>
                                    <input type="text" name="alternativePhone" value={formik.values.alternativePhone} onChange={(e) => formik.setFieldValue('alternativePhone', e.target.value.replace(/\D/g, '').slice(0, 10))} onBlur={formik.handleBlur} maxLength={10} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.alternativePhone && formik.errors.alternativePhone && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.alternativePhone}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Date of Birth <span className="text-red-500">*</span></label>
                                    <input type="date" name="dob" required value={formik.values.dob} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.dob && formik.errors.dob && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.dob}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Gender <span className="text-red-500">*</span></label>
                                    <select name="gender" required value={formik.values.gender} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm">
                                        <option value="">Select Gender</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {formik.touched.gender && formik.errors.gender && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.gender}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Profile Photo</label>
                                    <label className={`w-full py-4 px-4 border rounded-2xl flex items-center justify-center gap-2 cursor-pointer transition-all shadow-sm ${files.avatar ? 'bg-emerald-500 text-white font-bold border-emerald-500' : 'bg-white text-slate-400 border-slate-100 hover:border-violet-400'}`}>
                                        <Upload className="w-4 h-4" />
                                        <span className="text-[10px] font-black uppercase tracking-widest truncate">{files.avatar ? files.avatar.name.substring(0, 15) + '...' : 'Upload Photo'}</span>
                                        <input type="file" name="avatar" className="hidden" onChange={handleFileChange} accept="image/*" />
                                        {files.avatar && (
                                            <button type="button" onClick={(e) => handlePreview(e, files.avatar)} className="ml-1 bg-white/20 hover:bg-white/40 p-1 rounded-md text-white transition-colors" title="Preview Photo">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        )}
                                    </label>
                                </div>
                                <div className="col-span-full space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Current Address <span className="text-red-500">*</span></label>
                                    <textarea name="currentAddress" required value={formik.values.currentAddress} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all min-h-[80px] shadow-sm" />
                                    {formik.touched.currentAddress && formik.errors.currentAddress && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.currentAddress}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">City <span className="text-red-500">*</span></label>
                                    <input type="text" name="city" required value={formik.values.city} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.city && formik.errors.city && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.city}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">District <span className="text-red-500">*</span></label>
                                    <input type="text" name="district" required value={formik.values.district} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.district && formik.errors.district && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.district}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">State <span className="text-red-500">*</span></label>
                                    <select name="state" required value={formik.values.state} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm">
                                        <option value="">Select State</option>
                                        {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    {formik.touched.state && formik.errors.state && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.state}</div>)}
                                </div>
                                <div className="space-y-2">
                                     <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Pincode <span className="text-red-500">*</span></label>
                                     <input type="text" name="pincode" required value={formik.values.pincode} onChange={handlePincodeChange} onBlur={formik.handleBlur} placeholder="6-digit Pincode" className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" maxLength={6} />
                                     {formik.touched.pincode && formik.errors.pincode && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.pincode}</div>)}
                                 </div>

                                {/* Aadhaar */}
                                <div className="space-y-2 md:col-span-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Aadhaar Number <span className="text-red-500">*</span></label>
                                    <div className="relative group">
                                        <input type="text" name="aadhaarNumber" required value={formik.values.aadhaarNumber} onChange={(e) => formik.setFieldValue('aadhaarNumber', e.target.value.replace(/\D/g, '').slice(0, 12))} onBlur={formik.handleBlur} placeholder="12-Digit UID"
                                            className="w-full pl-5 pr-16 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm"
                                            maxLength={12} />
                                    {formik.touched.aadhaarNumber && formik.errors.aadhaarNumber && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.aadhaarNumber}</div>)}
                                        <label className={`absolute right-2 top-2 bottom-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-all ${files.aadhaarCard ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-violet-600 text-white shadow-lg shadow-violet-100'} hover:scale-[1.02] active:scale-95 z-10`}>
                                            <Upload className="w-4 h-4" />
                                            <span className="text-[9px] font-black uppercase tracking-widest hidden">{files.aadhaarCard ? 'Uploaded' : 'Upload'}</span>
                                            <input type="file" name="aadhaarCard" className="hidden" onChange={handleFileChange} accept=".pdf,image/*" />
                                        </label>
                                    </div>
                                    {files.aadhaarCard && (
                                        <p className="text-[9px] font-black text-emerald-600 uppercase ml-1 flex items-center gap-2">
                                            ✓ Aadhaar Attached ({files.aadhaarCard.name.substring(0, 15)}...)
                                            <button type="button" onClick={(e) => handlePreview(e, files.aadhaarCard)} className="text-emerald-700 hover:text-emerald-900 transition-colors" title="Preview Document">
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        </p>
                                    )}
                                </div>

                                {/* PAN */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">PAN Number <span className="text-red-500">*</span></label>
                                    <div className="relative group">
                                        <input type="text" name="panNumber" required value={formik.values.panNumber} onChange={(e) => formik.setFieldValue('panNumber', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10))} onBlur={formik.handleBlur} placeholder="ABCDE1234F"
                                            className="w-full pl-5 pr-16 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all uppercase shadow-sm"
                                            maxLength={10} />
                                    {formik.touched.panNumber && formik.errors.panNumber && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.panNumber}</div>)}
                                        <label className={`absolute right-2 top-2 bottom-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-all ${files.panCard ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-violet-600 text-white shadow-lg shadow-violet-100'} hover:scale-[1.02] active:scale-95 z-10`}>
                                            <Upload className="w-4 h-4" />
                                            <span className="text-[9px] font-black uppercase tracking-widest hidden">{files.panCard ? 'Uploaded' : 'Upload'}</span>
                                            <input type="file" name="panCard" className="hidden" onChange={handleFileChange} accept=".pdf,image/*" />
                                        </label>
                                    </div>
                                    {files.panCard && (
                                        <p className="text-[9px] font-black text-emerald-600 uppercase ml-1 flex items-center gap-2">
                                            ✓ PAN Attached ({files.panCard.name.substring(0, 15)}...)
                                            <button type="button" onClick={(e) => handlePreview(e, files.panCard)} className="text-emerald-700 hover:text-emerald-900 transition-colors" title="Preview Document">
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* SECTION 3: SETTLEMENT BANK DETAILS */}
                        <div className="bg-slate-50/35 border border-slate-100/80 p-6 md:p-10 rounded-[32px] space-y-6 md:space-y-8 shadow-sm">
                            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 shadow-sm">
                                    <CreditCard className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Settlement Bank Details</h3>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-0.5">Commission payout bank account information</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Bank Name <span className="text-red-500">*</span></label>
                                    <input type="text" name="bankName" required value={formik.values.bankName} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.bankName && formik.errors.bankName && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.bankName}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Account Holder Name <span className="text-red-500">*</span></label>
                                    <input type="text" name="accountName" required value={formik.values.accountName} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.accountName && formik.errors.accountName && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.accountName}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Account Number <span className="text-red-500">*</span></label>
                                    <input type="text" name="accountNumber" required value={formik.values.accountNumber} onChange={formik.handleChange} onBlur={formik.handleBlur} className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all shadow-sm" />
                                    {formik.touched.accountNumber && formik.errors.accountNumber && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.accountNumber}</div>)}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">IFSC Code <span className="text-red-500">*</span></label>
                                    <input type="text" name="ifscCode" required value={formik.values.ifscCode} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="ABCD0123456" className="w-full px-5 py-4 bg-white border border-slate-100 rounded-2xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500 transition-all uppercase shadow-sm" />
                                    {formik.touched.ifscCode && formik.errors.ifscCode && (<div className="text-red-500 text-[10px] mt-1 font-bold">{formik.errors.ifscCode}</div>)}
                                </div>
                            </div>

                            {/* Bank Document Upload - toggle style matching onboarding form */}
                            <div className="pt-4 border-t border-slate-100 space-y-5">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1 block">Select Document to Upload <span className="text-red-500">*</span></label>
                                <div className="flex gap-4">
                                    <button type="button" onClick={() => formik.setFieldValue('bankInfoMethod', 'cheque')}
                                        className={`flex-1 py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all border-2 ${formik.values.bankInfoMethod === 'cheque' ? 'bg-[#143E78] border-[#143E78] text-white shadow-xl shadow-blue-900/20' : 'bg-white border-slate-100 text-slate-400 hover:border-violet-300'}`}>
                                        Cancelled Cheque
                                    </button>
                                    <button type="button" onClick={() => formik.setFieldValue('bankInfoMethod', 'passbook')}
                                        className={`flex-1 py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all border-2 ${formik.values.bankInfoMethod === 'passbook' ? 'bg-[#143E78] border-[#143E78] text-white shadow-xl shadow-blue-900/20' : 'bg-white border-slate-100 text-slate-400 hover:border-violet-300'}`}>
                                        Bank Passbook
                                    </button>
                                </div>
                                {formik.values.bankInfoMethod === 'cheque' ? (
                                    <label className={`relative flex flex-col items-center justify-center gap-4 p-10 border-2 border-dashed rounded-[32px] cursor-pointer transition-all w-full ${files.cancelledCheque ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50/50 hover:bg-violet-50/30 hover:border-violet-300'}`}>
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-all ${files.cancelledCheque ? 'bg-emerald-500 text-white' : 'bg-white text-[#143E78]'}`}>
                                            <Upload className="w-7 h-7" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm font-black text-[#143E78]">Upload Cancelled Cheque</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Select PNG, JPG or PDF</p>
                                            {files.cancelledCheque && (
                                                <p className="mt-2 text-[10px] font-black text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full inline-flex items-center gap-2">
                                                    ✓ {files.cancelledCheque.name.substring(0, 22)}...
                                                    <button type="button" onClick={(e) => handlePreview(e, files.cancelledCheque)} className="text-emerald-700 hover:text-emerald-900 transition-colors" title="Preview Document">
                                                        <Eye className="w-3.5 h-3.5" />
                                                    </button>
                                                </p>
                                            )}
                                        </div>
                                        <input type="file" name="cancelledCheque" className="hidden" onChange={handleFileChange} accept=".pdf,image/*" />
                                    </label>
                                ) : (
                                    <label className={`relative flex flex-col items-center justify-center gap-4 p-10 border-2 border-dashed rounded-[32px] cursor-pointer transition-all w-full ${files.bankPassbook ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50/50 hover:bg-violet-50/30 hover:border-violet-300'}`}>
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-all ${files.bankPassbook ? 'bg-emerald-500 text-white' : 'bg-white text-[#143E78]'}`}>
                                            <Upload className="w-7 h-7" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm font-black text-[#143E78]">Upload Bank Passbook</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Front page details</p>
                                            {files.bankPassbook && (
                                                <p className="mt-2 text-[10px] font-black text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full inline-flex items-center gap-2">
                                                    ✓ {files.bankPassbook.name.substring(0, 22)}...
                                                    <button type="button" onClick={(e) => handlePreview(e, files.bankPassbook)} className="text-emerald-700 hover:text-emerald-900 transition-colors" title="Preview Document">
                                                        <Eye className="w-3.5 h-3.5" />
                                                    </button>
                                                </p>
                                            )}
                                        </div>
                                        <input type="file" name="bankPassbook" className="hidden" onChange={handleFileChange} accept=".pdf,image/*" />
                                    </label>
                                )}
                            </div>

                            {/* Digital Signature Upload */}
                            <div className="pt-4 border-t border-slate-100 space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1 block">Upload Digital Signature <span className="text-red-500">*</span></label>
                                <label className={`relative flex flex-col items-center justify-center gap-4 p-6 border-2 border-dashed rounded-[24px] cursor-pointer transition-all w-full ${files.signature ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50/50 hover:bg-violet-50/30 hover:border-violet-300'}`}>
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md transition-all ${files.signature ? 'bg-emerald-500 text-white' : 'bg-white text-[#143E78]'}`}>
                                        <Upload className="w-5 h-5" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs font-black text-[#143E78]">{files.signature ? files.signature.name : 'Upload Signature Image'}</p>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Select PNG, JPG or WEBP</p>
                                    </div>
                                    <input type="file" name="signature" className="hidden" onChange={handleFileChange} accept="image/*" />
                                </label>
                            </div>
                        </div>

                        {/* SECTION 4: SIGNATURE */}
                        <div className="bg-slate-50/35 border border-slate-100/80 p-6 md:p-10 rounded-[32px] space-y-6 md:space-y-8 shadow-sm">
                            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 shadow-sm">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Digital Signature</h3>
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-0.5">Please upload an image of your signature</p>
                                </div>
                            </div>
                            
                            <label className={`relative flex flex-col items-center justify-center gap-4 p-10 border-2 border-dashed rounded-[32px] cursor-pointer transition-all w-full ${files.signature ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50/50 hover:bg-violet-50/30 hover:border-violet-300'}`}>
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-all ${files.signature ? 'bg-emerald-500 text-white' : 'bg-white text-[#143E78]'}`}>
                                    <Upload className="w-7 h-7" />
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-black text-[#143E78]">Upload Signature</p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Select PNG, JPG or PDF</p>
                                    {files.signature && (
                                        <p className="mt-2 text-[10px] font-black text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full inline-flex items-center gap-2">
                                            ✓ {files.signature.name.substring(0, 22)}...
                                            <button type="button" onClick={(e) => handlePreview(e, files.signature)} className="text-emerald-700 hover:text-emerald-900 transition-colors" title="Preview Signature">
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        </p>
                                    )}
                                </div>
                                <input type="file" name="signature" className="hidden" onChange={handleFileChange} accept=".pdf,image/*" />
                            </label>
                        </div>

                        {/* Footer Actions */}
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 pb-4">
                            <button type="button" onClick={onClose} className="w-full sm:w-auto px-8 py-4 rounded-2xl border-2 border-slate-200 text-slate-500 font-black uppercase text-xs tracking-widest hover:bg-slate-50 transition-all">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-auto flex items-center justify-center gap-3 bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 text-white px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-xl shadow-violet-200 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
                            >
                                {loading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Registering Agent...
                                    </>
                                ) : (
                                    <>
                                        <BadgeCheck className="w-4 h-4" />
                                        Complete Agent Registration
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MarketingRegisterModal;
